// noinspection JSUnusedGlobalSymbols,ExceptionCaughtLocallyJS

import {
    FunctionResponse,
    GoogleGenAI,
    LiveServerMessage,
    Modality,
    Session as GeminiSession    
} from "@google/genai";

import {Sounds} from "../../Sounds";
import {Delay} from "../../Async";
import {GeminiAudioOutput} from "./GeminiAudioOutput"
import {GeminiAudioInput} from "./GeminiAudioInput";
import {GeminiUtilities} from "./GeminiUtilities";


class GeminiVoiceAgentImpl {
    private Client: GoogleGenAI | null = null;
    private Session: GeminiSession | null = null;
    private Status: string = "";
    private Dotnet: any = null;
    private Model: string = "";
    private Voice: string = "";
    private Prompt: string = "";
    private Tools: string = "";
    private Message = { Assistant: "", Reasoning: "", User: "" };
    private ApiKey: string | undefined = undefined;
    private Handle: string | undefined = undefined;
    private GoAway: boolean = false;
    private Reconnecting: boolean = false;
    private ReconnectedAt: number = 0;
    private Disconnecting: boolean = false;
    private Disconnected: boolean = false;
    private Connecting: boolean = false;

    private OnStatusChanged(status: string) {
        if (this.Reconnecting) return;
        if (status === this.Status) return;
        console.log("[Status] " + status);
        this.Status = status;
        this.Dotnet?.invokeMethodAsync("OnStatusChanged", status);
        if (this.Status === "Disconnected") Sounds.Beep();
    }

    public async Connect(apiKey: string, prompt: string, model: string, voice: string, voiceSpeed: number, tools: string, dotnet: any) {
        console.log("Connect", prompt, model, voice, voiceSpeed, tools);

        this.ApiKey = apiKey;
        this.Dotnet = dotnet;
        this.Model = model;
        this.Voice = voice;
        this.Prompt = prompt;
        this.Tools = tools;
        this.ReconnectedAt = 0;
        this.Handle = undefined;
        this.GoAway = false;
        this.Reconnecting = false;
        this.Disconnecting = false;
        this.Disconnected = false;
        
        this.OnStatusChanged("Connecting");

        this.Connecting = true;
        try {

            await this.CaptureAudioInput();
            await this.ConnectInternal();
            await Delay(100);

            if (!this.Session) {
                await this.Disconnect();
                return;
            }

            Sounds.Beep();
        } catch (e) {
            console.error(e);
            await this.Disconnect();            
        }
        finally {
            this.Connecting = false;
        }
    }

    private async ConnectInternal() {
        this.Client = new GoogleGenAI({ apiKey: this.ApiKey, httpOptions: { baseUrl: "wss://" + document.location.host + "/gemini"} });
        this.Session = await this.Client.live.connect({
            model: this.Model,
            callbacks: {
                onopen: () => this.OnStatusChanged("Listening"),
                onmessage: (message: LiveServerMessage) => this.OnMessage(message),
                onerror: (e: ErrorEvent) => {
                    console.error(e);
                },
                onclose: (e: CloseEvent) => {
                    if (this.Reconnecting) return;
                    if (this.Disconnecting) return;
                    if (this.Disconnected) return;
                    
                    this.GoAway = false;
                    try {
                        if (Date.now() - this.ReconnectedAt > 3000) {
                            this.ReconnectedAt = Date.now();
                            console.log("Socket closed unexpectedly, trying to reconnect.");
                            this.Reconnect();
                        }
                        else{
                            console.log("Socket closed unexpectedly, but we have already reconnected recently, so close the session completely.");
                            this.Disconnect();
                        }
                    }
                    catch (e) {
                        this.Disconnect();    
                    }
                },
            },
            config: {
                tools: GeminiUtilities.Tools(this.Tools),
                responseModalities: [Modality.AUDIO],
                inputAudioTranscription: {},
                outputAudioTranscription: {},
                systemInstruction: this.Prompt,
                speechConfig: {
                    voiceConfig: {prebuiltVoiceConfig: {voiceName: this.Voice}},
                },
                sessionResumption: {handle: this.Handle},                
                contextWindowCompression: {slidingWindow:{}},
                //thinkingConfig: {thinkingLevel: ThinkingLevel.HIGH},
                //enableAffectiveDialog: true,
                //proactivity: {proactiveAudio:true}
            },
        });
    }

    private async Reconnect() {
        console.log("Reconnecting");
        
        this.GoAway = false;
        this.Reconnecting = true;
        try { this.Session?.close(); } catch (e) { console.log(e); }
        try {
            await this.ConnectInternal();
            console.log("Reconnected successfully.");
        }
        finally {
            this.Reconnecting = false;
        }
    }

    private async CaptureAudioInput() {
        const audioStarted = await GeminiAudioInput.Capture((data: string, mime: string) => {
            try {
                if (this.Reconnecting) return;
                if (this.Disconnecting) return;
                if (this.Disconnected) return;
                if (this.Connecting) return;
                
                this.Session?.sendRealtimeInput({
                    audio: {
                        data: data,
                        mimeType: mime,
                    },
                });
            }
            catch {
                
            }
        });

        if (!audioStarted) {
            this.OnStatusChanged("Disconnected");
            throw "Failed to start audio capture";
        }
    }

    private async OnMessage(message: LiveServerMessage) {

        // Reconnect required
        if (message.goAway) {
            console.log('Go away', message);
            this.GoAway = true;
            return;
        }
        
        // Session resumption handle
        if (message.sessionResumptionUpdate && message.sessionResumptionUpdate.newHandle && message.sessionResumptionUpdate.resumable){
            console.log("Session resumption update", message);
            this.Handle = message.sessionResumptionUpdate.newHandle;
            
            // NOTE: presumably it is a good time to 
            // reconnect, but I am not 100% sure
            if (this.GoAway){
                console.log("Trying to reconnect as we have a fresh session resumption handle", message);
                this.GoAway = false;
                await this.Reconnect();
            }
            return;
        }
    
        // Audio parts
        if (message.serverContent?.modelTurn?.parts) {
            for (const part of message.serverContent.modelTurn.parts) {
                const audio = part.inlineData;
                if (audio?.data) {
                    try {
                        await GeminiAudioOutput.Play(audio.data, audio.mimeType);
                    } catch (e) {
                        console.error("Failed to play audio", e);
                    }
                    this.OnStatusChanged("Speaking");
                }

                const reasoning = part.thought === true && part.text != null;
                if (reasoning) {
                    this.Message.Reasoning = (this.Message.Reasoning + " " + part.text).trim();
                    this.OnStatusChanged("Thinking");
                }
            }

            // Also this means we can complete user message
            if (this.Message.User.trim().length > 1) {
                this.Dotnet?.invokeMethodAsync("OnMessage", "User", this.Message.User);
                this.Message.User = "";
            }
        }

        // Interruption
        const interrupted = message.serverContent?.interrupted;
        if (interrupted) {
            this.Message.Assistant = "";
            GeminiAudioOutput.Stop();
            this.OnStatusChanged("Listening");
            console.log("Interrupted", message);
        }

        // Turn complete
        const turn = message.serverContent?.turnComplete;
        if (turn) {
            if (this.Message.User.length > 1) this.Dotnet?.invokeMethodAsync("OnMessage", "User", this.Message.User);
            if (this.Message.Reasoning.length > 1) this.Dotnet?.invokeMethodAsync("OnMessage", "Reasoning", this.Message.Reasoning);
            if (this.Message.Assistant.length > 1) this.Dotnet?.invokeMethodAsync("OnMessage", "Assistant", this.Message.Assistant);
            this.Message.User = "";
            this.Message.Reasoning = "";
            this.Message.Assistant = "";
            
            if (this.GoAway) {
                this.GoAway = false;
                await this.Reconnect();
            }

            this.OnStatusChanged("Listening");
        }

        // Transcriptions
        const user = message.serverContent?.inputTranscription?.text;
        if (user) this.Message.User = this.Message.User + user;
        const assistant = message.serverContent?.outputTranscription?.text;
        if (assistant) this.Message.Assistant = (this.Message.Assistant + " " + assistant).trim();

        // Tool calls
        if (message.toolCall?.functionCalls) {
            const responses: FunctionResponse[] = [];
            for (const call of message.toolCall.functionCalls) {
                console.log("Tool call", call.name, call.args);
                try {
                    const result = await this.Dotnet.invokeMethodAsync("OnToolExecute", call.name, JSON.stringify(call.args));
                    responses.push({
                        name: call.name,
                        id: call.id,
                        response: { "output": result }
                    });
                }
                catch (e) {
                    console.error("Tool call failed", e);
                    responses.push({
                        name: call.name,
                        id: call.id,
                        response: { "error": JSON.stringify(e) }
                    });
                }
            }
            this.Session?.sendToolResponse({ functionResponses: responses });
        }
        
        // TODO: Tool call cancellations
        // TODO: Usage metadata
    }

    public async Update(prompt: string, tools: string, clearHistory: boolean): Promise<void> {
        if (!this.Session) return;

        try {
            console.log("Update", prompt, tools, clearHistory);
            this.Prompt = prompt;
            this.Tools = tools;
            if (clearHistory) console.log("Clear history is not implemented");
            this.GoAway = true;
        } catch (e) {
            console.error(e);
        }
    }

    public async Send(role: string, text: string): Promise<void> {
        if (!this.Session) return;

        if (role === "tool") {
            console.error("Sending tool calls is not supported");
            return;
        }

        try {
            console.log("Send", role, text);

            // Send a text turn. SDK supports client content; shape may differ by version.
            // This aligns with typical genai live "clientContent" usage.
            this.Session.sendClientContent({
                turns: [
                    {
                        role: role,
                        parts: [{ text }],
                    },
                ],
                turnComplete: true
            });
        } catch (e) {
            console.error(e);
        }
    }

    public async Respond(phrase: string) {
        // Gemini Live doesn't have an exact response.create analogue.
        // Best-effort: send an instruction as a user turn.
        if (phrase == null) return;
        await this.Send("user", `Say this: "${phrase}".`);
    }

    public Mute(muted: boolean) {
        GeminiAudioInput.Mute(muted);
    }

    public async Disconnect(): Promise<void> {
        console.trace("Disconnecting");
        this.Disconnecting = true;
        this.Connecting = false;
        try {
            GeminiAudioOutput.Stop();
            GeminiAudioInput.Stop();

            try {
                this.Session?.close();
            } catch (e) {
                console.warn("Failed to close session", e);
            }            
            
            this.Session = null;
        } catch (e) {
            console.error(e);
        }
        finally {
            this.Disconnecting = false;
            this.Disconnected = true;
        }
        this.OnStatusChanged("Disconnected");
    }
}

export const GeminiVoiceAgent = new GeminiVoiceAgentImpl();
